import React from 'react';
import { Badge, Skeleton, Text } from '@/components/ui';
import {
  IconDownload,
  IconEdit,
  IconEye,
  IconTrash,
  IconUpload,
  IconUser,
  IconSettings,
  IconFileText,
} from '@tabler/icons-react';

interface ActionCellProps {
  value?: unknown;
  actionMap?: Record<
    string,
    {
      color?: React.ComponentProps<typeof Badge>['color'];
      icon?: React.ReactNode;
      label?: string;
    }
  >;
  render?: () => React.ReactNode;
}

// Default action mapping based on common action patterns
const getDefaultActionConfig = (action: string) => {
  const actionLower = action.toLowerCase();
  
  // Determine color and icon
  let color: React.ComponentProps<typeof Badge>['color'] = 'default';
  let icon: React.ReactNode = undefined;
  
  if (actionLower.includes('upload') || actionLower.includes('create')) {
    color = 'default';
    icon = <IconUpload size={12} />;
  } else if (actionLower.includes('delete') || actionLower.includes('remove')) {
    color = 'error';
    icon = <IconTrash size={12} />;
  } else if (actionLower.includes('view') || actionLower.includes('read')) {
    color = 'success';
    icon = <IconEye size={12} />;
  } else if (
    actionLower.includes('modify') ||
    actionLower.includes('update') ||
    actionLower.includes('edit')
  ) {
    color = 'default';
    icon = <IconEdit size={12} />;
  } else if (actionLower.includes('download')) {
    color = 'primary';
    icon = <IconDownload size={12} />;
  } else if (
    actionLower.includes('user') ||
    actionLower.includes('login') ||
    actionLower.includes('auth')
  ) {
    color = 'default';
    icon = <IconUser size={12} />;
  } else if (actionLower.includes('setting') || actionLower.includes('config')) {
    color = 'default';
    icon = <IconSettings size={12} />;
  } else {
    icon = <IconFileText size={12} />;
  }

  return { color, icon, label: action };
};

function ActionCell({ value, actionMap, render }: ActionCellProps) {
  if (render) {
    return <>{render()}</>;
  }

  if (value === null || value === undefined || value === '') {
    return (
      <Text size="2" color="gray" className="data-cell__action">
        —
      </Text>
    );
  }

  const action = String(value);
  const config = actionMap?.[action] || getDefaultActionConfig(action);

  return (
    <Badge
      size="small"
      variant="surface"
      color={config.color}
      startIcon={config.icon}
      className="data-cell__action"
    >
      {config.label || action}
    </Badge>
  );
}

export function ActionCellSkeleton() {
  return (
    <Skeleton style={{ width: '120px', height: '24px', borderRadius: '4px' }} />
  );
}

export default ActionCell;

